import { defineStore } from 'pinia'
import { ref } from 'vue'

export type DisplayDensity = 'comfortable' | 'dense'

// Sentinel for "no preference set yet" — used by views and composables
// that read `currentUser?.display_density` while the session is still
// loading or the user is signed out. Centralised so a future change to
// the implicit-default (e.g. respect a `prefers-reduced-motion`-style
// media query) lands in one place.
export const DEFAULT_DENSITY: DisplayDensity = 'comfortable'

export interface CurrentUser {
  id: number
  email_address: string
  admin: boolean
  last_seen_log_at: string | null
  display_density: DisplayDensity
}

// Mutable user-preference fields that `updatePreferences` accepts.
// Extending: add the new field here AND in the controller's permitted
// params (`session_preferences_params`). The Pinia store action is
// generic; only the type and the controller need updates.
export type UserPreferencePatch = Partial<Pick<CurrentUser, 'display_density'>>

// Minimal shape check for a parsed /session body (#1251, #1255) — cheaper
// and more targeted than a full schema, but enough to stop a malformed 2xx
// (version skew, a proxy error page) from being cast straight into
// `currentUser` and crashing the first component that reads a missing field.
function isValidCurrentUser(value: unknown): value is CurrentUser {
  return typeof value === 'object' && value !== null && 'id' in value && 'email_address' in value
}

/**
 * Tracks the signed-in user. Talks to the Rails session endpoint at /session
 * (not /api/v1/...), which is where Rails 8's built-in authentication
 * generator scaffolded the session routes. Cookies are session-based, so
 * every fetch opts in with `credentials: 'include'`.
 */
export const useSessionStore = defineStore('session', () => {
  const currentUser = ref<CurrentUser | null>(null)
  // `checked` flips to true after the first fetchCurrentUser() so the router
  // guard doesn't re-issue the request on every navigation.
  const checked = ref(false)

  async function fetchCurrentUser(): Promise<CurrentUser | null> {
    const res = await fetch('/session', {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
    // GET /session returns 200 with either a user payload or null. This runs
    // on every route's auth guard, so a malformed body degrades to "signed
    // out" here rather than throwing — surfacing an error would break
    // navigation app-wide instead of just the one page that hit it.
    const body: unknown = res.ok ? await res.json() : null
    currentUser.value = isValidCurrentUser(body) ? body : null
    checked.value = true
    return currentUser.value
  }

  async function signIn(email: string, password: string): Promise<CurrentUser> {
    const res = await fetch('/session', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ email_address: email, password }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}) as { error?: string })
      throw new Error(body.error || `sign-in failed (${res.status})`)
    }
    const body: unknown = await res.json()
    if (!isValidCurrentUser(body)) throw new Error('Unexpected response from sign-in')
    currentUser.value = body
    return currentUser.value
  }

  async function signOut(): Promise<void> {
    await fetch('/session', { method: 'DELETE', credentials: 'include' })
    currentUser.value = null
  }

  // Marks the wiki Log as read for the signed-in user. Fire-and-forget
  // from the caller — on a clean response we patch currentUser locally
  // so the nav badge clears immediately, without a refetch.
  async function markLogSeen(): Promise<void> {
    const res = await fetch('/api/v1/log/seen', {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok || !currentUser.value) return
    const body = (await res.json()) as { last_seen_log_at: string }
    currentUser.value = { ...currentUser.value, last_seen_log_at: body.last_seen_log_at }
  }

  // Update mutable preferences on the signed-in user. Optimistically
  // applies the patch to local state so the UI reflects the change
  // before the API responds; rolls back on failure. Returns a promise
  // so callers can await for error handling, but the optimistic flip
  // means most call sites don't need to.
  async function updatePreferences(patch: UserPreferencePatch): Promise<void> {
    if (!currentUser.value) return
    const previous = currentUser.value
    currentUser.value = { ...previous, ...patch }
    try {
      const res = await fetch('/session', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ user: patch }),
      })
      if (!res.ok) {
        currentUser.value = previous
        return
      }
      const body: unknown = await res.json()
      if (!isValidCurrentUser(body)) throw new Error('Unexpected response from session update')
      currentUser.value = body
    } catch {
      currentUser.value = previous
    }
  }

  return { currentUser, checked, fetchCurrentUser, signIn, signOut, markLogSeen, updatePreferences }
})
